/** @jsx jsx */

import type { ReactNode } from 'react';
import { Children, useMemo } from 'react';
import type { BoxProps } from '@balance-web/box';
import { Box } from '@balance-web/box';
import type { ResponsiveProp } from '@balance-web/core';
import { jsx } from '@balance-web/core';
import { useMediaQuery } from '@balance-web/media-query';
import type { BalanceTheme } from '@balance-web/theme';
import { useTheme } from '@balance-web/theme';
import { forwardRefWithAs } from '@balance-web/utils';

const alignVerticalMap = {
  top: 'flex-start',
  center: 'center',
  bottom: 'flex-end',
  stretch: 'stretch',
} as const;

export type ColumnsProps = {
  /** Vertically align items within the container. */
  alignY?: keyof typeof alignVerticalMap;
  /** Elements representing each column. */
  children: ReactNode;
  /** At which breakpoint, if any, should the columns collapse. */
  collapse?: keyof BalanceTheme['breakpoints'];
  /** The size of the gap between each column. */
  gap?: ResponsiveProp<keyof BalanceTheme['spacing']>;
  /** Define the relative width of each column. By default each column is the same width. */
  template?: number[] | (null | number[])[];
} & BoxProps;

function isNonResponsiveTemplate(
  template: ColumnsProps['template']
): template is number[] {
  return !!template && !Array.isArray(template[0]);
}

export const Columns = forwardRefWithAs<'div', ColumnsProps>(
  (
    { alignY = 'top', collapse, gap = 'none', template, ...boxProps },
    forwardedRef
  ) => {
    const count = Children.toArray(boxProps.children).length; // Children.count() counts falsey children
    const { spacing } = useTheme();
    const { mq, mapResponsiveProp, maxBreak } = useMediaQuery();
    const alignItems = alignVerticalMap[alignY];
    const gridTemplateColumns = useMemo(() => {
      if (!template) {
        return [`repeat(${count}, 1fr)`]; // Default to equal width columns
      }

      function toGridFraction(item: number | null) {
        return `${item}fr`;
      }

      const output = [];

      if (isNonResponsiveTemplate(template)) {
        return [template.map(toGridFraction).join(' ')];
      }

      for (const item of template) {
        if (Array.isArray(item)) {
          output.push(item.map(toGridFraction).join(' '));
          continue;
        }

        output.push(item);
      }

      return output;
    }, [count, template]);

    const collapseStyles = collapse
      ? {
          [maxBreak(collapse)]: {
            gridTemplateColumns: 'none',
          },
        }
      : {};

    return (
      <Box
        ref={forwardedRef}
        css={mq({
          alignItems,
          display: 'grid',
          gap: mapResponsiveProp(gap, spacing),
          gridTemplateColumns: gridTemplateColumns,
          ...collapseStyles,
        })}
        {...boxProps}
      />
    );
  }
);
